import { FC, useMemo, useState } from 'react';

import { Address } from '@ravago/shared/page-data/models/elements/address.model';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BoxItem } from '@ravago/shared/radiance/components/box/components/BoxItem/BoxItem';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { DatePicker } from '@ravago/shared/radiance/components/input-field/components/DatePicker/DatePicker';
import { InputGroup } from '@ravago/shared/radiance/components/input-field/components/InputGroup/InputGroup';
import { Multiselect, MultiselectOption } from '@ravago/shared/radiance/components/input-field/components/Multiselect/Multiselect';
import { Switch } from '@ravago/shared/radiance/components/switch/components/Switch/Switch';
import { useTranslations } from '@ravago/shared/translations/client';

import uniqBy from 'lodash/uniqBy';
import { firstBy } from 'thenby';

import { TrackingUtils } from '../../../../utils/tracking/tracking.utils';
import { OrderFilters } from '../models/order-filters';

const getFilterCount = (filters: OrderFilters) => {
   let count = 0;
   if (filters.deliveryDateFrom) count += 1;
   if (filters.deliveryDateTo) count += 1;
   if (filters.deliveryAddressIds.length > 0) count += 1;
   if (filters.callOff) count += 1;
   return count;
};

export type Props = {
   filters: OrderFilters;
   hasCallOffs: boolean;
   orderAddresses: Address[];
   disabled: boolean;
   onSearchOrFilter: (value: OrderFilters) => void;
};

export const OrderOverviewSearchFilter: FC<Props> = ({ filters, orderAddresses, hasCallOffs, disabled, onSearchOrFilter }) => {
   const translate = useTranslations();
   const [showFilters, setShowFilters] = useState(() => getFilterCount(filters) > 0);

   const addressOptions = useMemo<MultiselectOption[]>(
      () =>
         uniqBy(orderAddresses, 'id')
            .map((value) => ({
               checked: filters.deliveryAddressIds.includes(value.id),
               id: value.id.toString(),
               label: value.name
            }))
            .sort(firstBy('label', 'asc')),
      [filters, orderAddresses]
   );

   const filterCount = getFilterCount(filters);

   const resetMultiselectAddressFilters = () => {
      onSearchOrFilter({
         ...filters,
         deliveryAddressIds: []
      });
   };

   const resetMultiselectAddress = () => {
      onSearchOrFilter({
         ...filters,
         deliveryAddressIds: []
      });
   };

   const resetFilters = () => {
      resetMultiselectAddressFilters();
      onSearchOrFilter({
         search: '',
         deliveryDateFrom: '',
         deliveryDateTo: '',
         callOff: false,
         deliveryAddressIds: []
      });
   };

   const trackSearch = (value: string) => {
      TrackingUtils.track('click_orders_search_button', {
         searchTerm: value
      });
   };

   const handleSearch = (value: string) => {
      trackSearch(value);
      onSearchOrFilter({ ...filters, search: value });
   };

   const handleDeliveryDateFromChange = (date: string) => {
      onSearchOrFilter({ ...filters, deliveryDateFrom: date });
   };

   const handleDeliveryDateToChange = (date: string) => {
      onSearchOrFilter({ ...filters, deliveryDateTo: date });
   };

   const handleAddressChange = (options: MultiselectOption[]) => {
      onSearchOrFilter({
         ...filters,
         deliveryAddressIds: options.filter((option) => option.checked).map((option) => Number(option.id))
      });
   };

   const handleCallOffChange = (value: boolean) => {
      onSearchOrFilter({
         ...filters,
         callOff: value
      });
   };

   return (
      <Box direction="column" items="center" margin={{ bottom: 'md' }}>
         <Box justify="between" width="100%">
            <InputGroup
               id="orders-search-input"
               actionIcon="search"
               autocomplete="off"
               clearable
               label={translate('my-orders.order-overview-search-placeholder')}
               onEnter={handleSearch}
               onActionClick={handleSearch}
               onClear={() => handleSearch('')}
               defaultValue={filters.search}
               width="100%"
               disabled={disabled}
            />
            <Box gap="none">
               <BreakpointSwitch includeViews={['desktop']}>
                  <Button
                     id="orders-toggle-filter-btn"
                     leadingIcon="sliders-h"
                     type="button"
                     variant="tertiary"
                     onClick={() => setShowFilters(!showFilters)}
                     disabled={disabled}
                  >
                     {showFilters ? translate('my-orders.order-overview-filter-hide') : translate('my-orders.order-overview-filter-show')}
                  </Button>
               </BreakpointSwitch>
               <BreakpointSwitch excludeViews={['desktop']}>
                  <IconButton
                     backdrop
                     icon="sliders-h"
                     onClick={() => setShowFilters(!showFilters)}
                     type="button"
                     variant="on-light"
                     disabled={disabled}
                  />
               </BreakpointSwitch>

               <Box position="relative" left={-15} margin="none" spacing="none" gap="none">
                  {filterCount > 0 && <Badge variant="primary">{filterCount}</Badge>}
               </Box>
            </Box>
         </Box>
         {showFilters && (
            <Box justify="between" items="center" width="100%" wrap="wrap" gap="xs">
               <BoxItem grow={1} order={0}>
                  <DatePicker
                     id="order-overview-filter-date-picker-from"
                     label={translate('my-orders.order-overview-filter-date-from')}
                     value={filters.deliveryDateFrom}
                     onChange={handleDeliveryDateFromChange}
                  />
               </BoxItem>
               <BoxItem grow={1} order={0}>
                  <DatePicker
                     id="order-overview-filter-date-picker-until"
                     label={translate('my-orders.order-overview-filter-date-until')}
                     value={filters.deliveryDateTo}
                     onChange={handleDeliveryDateToChange}
                  />
               </BoxItem>
               <BoxItem grow={0} order={0}>
                  <Multiselect
                     clearText="Clear"
                     id="order-overview-filter-delivery-address"
                     label={translate('my-orders.order-overview-filter-delivery-address')}
                     onChange={handleAddressChange}
                     onClear={resetMultiselectAddress}
                     options={addressOptions}
                  />
               </BoxItem>
               <BoxItem grow={1} order={0}>
                  {hasCallOffs && (
                     <Switch
                        id="order-overview-filter-call-off"
                        label={translate('my-orders.order-overview-filter-callOff')}
                        checked={filters.callOff}
                        onChange={handleCallOffChange}
                     />
                  )}
               </BoxItem>

               <Button type="button" variant="secondary" onClick={resetFilters} disabled={filterCount === 0}>
                  {translate('my-orders.order-overview-filter-reset')}
               </Button>
            </Box>
         )}
      </Box>
   );
};
